import {
  booleanAttribute,
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  type OnInit,
} from '@angular/core';
import { Badge } from '../badge/badge';
import { Icon, type IconInput } from '../icon/icon';

/** Visual weight. Maps to Figma's `Style` property. */
export type ButtonVariant = 'primary' | 'secondary' | 'tertiary' | 'ghost';

/** Maps to Figma's `Size` property. */
export type ButtonSize = 'xs' | 'sm' | 'md' | 'lg';

/**
 * The design-system button.
 *
 * An attribute selector on a native `<button>`, not a wrapper element: the
 * consumer keeps real button semantics — `type`, `disabled`, form participation,
 * focus order, Enter/Space activation — instead of us re-implementing them
 * around a nested button.
 *
 * ```html
 * <button ui-button variant="primary" size="lg">Save</button>
 * <button ui-button variant="secondary" leadIcon="plus" badge="New">Add item</button>
 * <button ui-button destructive leadIcon="trash-2" iconOnly ariaLabel="Delete" ></button>
 * ```
 *
 * Figma models this as 256 variants across Style × Size × Type × Target × State.
 * Four of those five are inputs here; `State` is not. Hover, focus and disabled
 * are what the platform already gives us (`:hover`, `:focus-visible`,
 * `:disabled`), so making them inputs would let a caller render a lie — a button
 * that looks disabled but is not, or looks focused when it is not.
 */
@Component({
  selector: 'button[ui-button]',
  imports: [Icon, Badge],
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrl: './button.scss',
  host: {
    // No static `class` alongside this: `[class]` replaces the attribute
    // wholesale, so the base class is part of hostClasses() instead.
    '[class]': 'hostClasses()',
    '[attr.aria-label]': 'ariaLabel() || null',
  },
  template: `
    @if (leadIcon()) {
      <ui-icon [name]="leadIcon()!" [strokeWidth]="strokeWidth()" />
    }

    @if (!iconOnly()) {
      <span class="ui-button__label"><ng-content /></span>
    }

    @if (badge()) {
      <ui-badge color="white">{{ badge() }}</ui-badge>
    }

    @if (tailIcon()) {
      <ui-icon [name]="tailIcon()!" [strokeWidth]="strokeWidth()" />
    }
  `,
})
export class Button implements OnInit {
  readonly variant = input<ButtonVariant>('primary');
  readonly size = input<ButtonSize>('md');

  /** Figma `Target=Destructive` — the danger treatment of the chosen variant. */
  readonly destructive = input(false, { transform: booleanAttribute });

  /** Figma `Type=Icon only`: square, no label. Requires `ariaLabel`. */
  readonly iconOnly = input(false, { transform: booleanAttribute });

  /** Leading icon. Accepts a sprite name or a semantic alias. */
  readonly leadIcon = input<IconInput>();

  /** Trailing icon. Accepts a sprite name or a semantic alias. */
  readonly tailIcon = input<IconInput>();

  /** Optional badge text rendered after the label. */
  readonly badge = input<string>();

  /**
   * Accessible name. Required when `iconOnly` — without it the button announces
   * nothing, which is the a11y bug we already fixed once in `<ui-icon>`.
   */
  readonly ariaLabel = input<string>();

  /** Figma ships 1.5 and 2; buttons use 2 unless a caller needs otherwise. */
  readonly strokeWidth = input(2);

  protected readonly hostClasses = computed(() => {
    const classes = ['ui-button', `ui-button--${this.variant()}`, `ui-button--${this.size()}`];
    if (this.destructive()) classes.push('ui-button--destructive');
    if (this.iconOnly()) classes.push('ui-button--icon-only');
    return classes.join(' ');
  });

  ngOnInit(): void {
    // Inputs are resolved by ngOnInit, so this is the earliest honest place to
    // check. Fails loudly rather than shipping a control that announces nothing —
    // the same bug we already fixed once in <ui-icon>.
    if (this.iconOnly() && !this.ariaLabel()) {
      throw new Error(
        '<button ui-button iconOnly> requires an ariaLabel — an icon-only button has ' +
          'no text to announce. Pass ariaLabel="…" describing the action.',
      );
    }
  }
}
